import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { LocationsApi } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi, getApiErrorMessage } from "@/lib/utils";
import { useToast } from "@/components/feedback";

export const useLocationDelete = () => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const toast = useToast();

  const { mutate, isPending } = useMutation({
    mutationFn: async (locationId: number) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(LocationsApi, accessToken);
      return service.apiLocationsIdDelete({ id: locationId });
    },
    onSuccess: async (_data, locationId) => {
      queryClient.removeQueries({ queryKey: [QueryKeys.Locations, locationId] });
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.Locations] });
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.ClassRooms] });
      toast({
        title: "Location deleted",
        description: "Location has been deleted successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: async (error: unknown) => {
      const errorMessage = await getApiErrorMessage(
        error,
        "This location is still in use and cannot be deleted. Remove or reassign linked classrooms and classes first.",
      );
      toast({
        title: "Cannot delete location",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending };
};
